import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LoginDialog({ open, requiredRole, onGoHome }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const roleLabel =
    requiredRole === 'gestion'
      ? 'Personal de Valoriza'
      : 'Técnicos Municipales (STM)';
  const hint =
    requiredRole === 'gestion'
      ? 'admin@valoriza.com / gestion123'
      : 'tecnico@alcazares.es / tecnicos123';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Introduce email y contraseña.');
      return;
    }
    setLoading(true);
    setError('');
    const ok = login(email, password, requiredRole);
    if (!ok) {
      setError(
        'Credenciales incorrectas o acceso no autorizado para este perfil.'
      );
    }
    setLoading(false);
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="login-dialog"
        className="max-w-md [&>button:last-child]:hidden"
        aria-labelledby="login-title"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#CFE3F5] flex items-center justify-center">
              <Lock size={24} className="text-[#1E5AA8]" aria-hidden="true" />
            </div>
          </div>
          <DialogTitle
            id="login-title"
            className="text-center font-display text-2xl text-[#0A2540]"
          >
            Acceso restringido
          </DialogTitle>
          <DialogDescription className="text-center text-[#6F8AA8] text-sm">
            Esta área es exclusiva para <strong>{roleLabel}</strong>.
            Introduce tus credenciales para continuar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="login-email" className="text-xs font-medium text-[#0A2540]">
              Email
            </Label>
            <Input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.es"
              className="border-[#CFE3F5] focus:border-[#1E5AA8] focus:ring-[#1E5AA8]"
              autoComplete="email"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="login-password" className="text-xs font-medium text-[#0A2540]">
              Contraseña
            </Label>
            <div className="relative">
              <Input
                id="login-password"
                data-testid="login-password-input"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="border-[#CFE3F5] focus:border-[#1E5AA8] pr-10"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F8AA8] hover:text-[#0A2540] transition-colors"
                aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPwd ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
              </button>
            </div>
          </div>

          {error && (
            <p role="alert" className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#0A2540] text-white hover:bg-[#1E5AA8] transition-colors"
          >
            {loading ? 'Verificando…' : 'Entrar'}
          </Button>
        </form>

        {/* Demo hint */}
        <div className="mt-4 bg-[#FAFCFE] rounded-xl px-4 py-3 border border-[#CFE3F5]">
          <p className="text-xs text-[#6F8AA8] font-medium mb-1">Credenciales de demo:</p>
          <code className="text-xs text-[#1E5AA8] font-mono">{hint}</code>
        </div>

        {/* Go home */}
        <button
          onClick={onGoHome}
          className="mt-3 w-full flex items-center justify-center gap-2 text-xs text-[#6F8AA8] hover:text-[#0A2540] transition-colors py-2"
        >
          <ArrowLeft size={13} aria-hidden />
          Volver al inicio
        </button>
      </DialogContent>
    </Dialog>
  );
}
